import { useState, useEffect } from 'react'
import AnimatedNumber from './AnimatedNumber'

// Mock executive data
const EXECUTIVE_DATA = {
  ceoName: 'Zakariya (Z)',
  company: 'Sanajim Technologies',
  lastUpdated: new Date().toISOString(),
  
  criticalBlockers: [
    { id: 1, title: 'Google Maps API Key Required', project: 'Rafiq', impact: 'Blocks V1.0 deployment', priority: 'P0', assignedTo: 'Z', status: 'awaiting_decision' },
    { id: 2, title: 'Labor Portal Launch Strategy', project: 'Labor Portal', impact: 'Market entry timing', priority: 'P1', assignedTo: 'Marketing Agent', status: 'in_review' },
    { id: 3, title: 'Muwafaq Pricing Finalization', project: 'Muwafaq', impact: 'Revenue model', priority: 'P1', assignedTo: 'CFO Agent', status: 'analysis_complete' },
  ],
  
  nextMilestones: [
    { id: 1, project: 'Labor Portal', milestone: 'Web Admin Dashboard', dueDate: '2026-04-17', confidence: 'high', dependencies: 'None' },
    { id: 2, project: 'Rafiq', milestone: 'Google Maps Integration', dueDate: '2026-04-15', confidence: 'blocked', dependencies: 'API Keys from Z' },
    { id: 3, project: 'Sahha', milestone: 'Clinic Reports Module', dueDate: '2026-04-18', confidence: 'medium', dependencies: 'NPHIES integration complete' },
    { id: 4, project: 'Muwafaq', milestone: 'Auto Evidence Collection', dueDate: '2026-04-20', confidence: 'high', dependencies: 'None' },
  ],
  
  resourceAllocation: {
    totalAgents: 7,
    activeAgents: 5,
    byProject: {
      'Labor Portal': { agents: 3, tokens: 375000, priority: 'highest' },
      'Rafiq': { agents: 2, tokens: 294000, priority: 'high' },
      'Muwafaq': { agents: 3, tokens: 225000, priority: 'medium' },
      'Sahha': { agents: 2, tokens: 195000, priority: 'medium' },
      'Dashboard': { agents: 2, tokens: 96000, priority: 'low' },
    }
  },
  
  financialSnapshot: {
    monthlyBurn: 0, // SAR
    projectedRevenue: 150000, // SAR/month at 15 customers
    breakEvenCustomers: 15,
    currentCustomers: 0,
    runway: 'infinite', // Zero burn rate
    nextFundingNeed: 'None required',
  },
  
  strategicPriorities: [
    { id: 1, priority: 'Launch Labor Portal MVP', timeline: 'Q2 2026', status: 'on_track', owner: 'Lead Dev Agent' },
    { id: 2, priority: 'Secure first 3 paying customers', timeline: 'Q3 2026', status: 'planning', owner: 'Marketing Agent' },
    { id: 3, priority: 'Expand Muwafaq to ISO 27001', timeline: 'Q4 2026', status: 'research', owner: 'CTO Agent' },
    { id: 4, priority: 'Hire first human employee', timeline: 'Q1 2027', status: 'deferred', owner: 'CEO' },
  ],
  
  quickActions: [
    { id: 1, action: 'Review Rafiq API Keys', urgency: 'immediate', timeRequired: '5 mins' },
    { id: 2, action: 'Approve Labor Portal marketing budget', urgency: 'today', timeRequired: '10 mins' },
    { id: 3, action: 'Review Muwafaq pricing proposal', urgency: 'this week', timeRequired: '15 mins' },
  ]
}

// Mock API function
const fetchExecutiveData = async () => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(EXECUTIVE_DATA)
    }, 500)
  })
}

export default function ExecutiveOverview() {
  const [data, setData] = useState(EXECUTIVE_DATA)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        const executiveData = await fetchExecutiveData()
        setData(executiveData || EXECUTIVE_DATA)
      } catch (error) {
        console.error('Failed to load executive data:', error)
        setData(EXECUTIVE_DATA) // fallback to prevent null crash
      } finally {
        setLoading(false)
      }
    }

    loadData()
    const interval = setInterval(loadData, 60000) // Update every minute
    return () => clearInterval(interval)
  }, [])

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'P0': return '#EF4444'
      case 'P1': return '#F59E0B'
      case 'P2': return '#3B82F6'
      default: return '#64748B'
    }
  }

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return '#22C55E'
      case 'medium': return '#F59E0B'
      case 'low': return '#EF4444'
      case 'blocked': return '#EF4444'
      default: return '#64748B'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track': return '#22C55E'
      case 'planning': return '#F59E0B'
      case 'research': return '#3B82F6'
      case 'deferred': return '#64748B'
      case 'awaiting_decision': return '#F59E0B'
      case 'in_review': return '#3B82F6'
      case 'analysis_complete': return '#22C55E'
      default: return '#64748B'
    }
  }

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'immediate': return '#EF4444'
      case 'today': return '#F59E0B'
      case 'this week': return '#3B82F6'
      default: return '#64748B'
    }
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-white/[0.04] rounded-lg w-64 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-96 bg-white/[0.04] rounded-[14px] animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* CEO Header */}
      <div className="rounded-[14px] bg-gradient-to-r from-[#8B5CF6]/20 to-[#C084FC]/10 border border-[#8B5CF6]/30 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">👑 Executive Overview</h1>
            <p className="text-sm text-[#94A3B8]">CEO-focused dashboard for {data.ceoName}</p>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-white">{data.company}</div>
            <div className="text-xs text-[#94A3B8]">
              Last updated: {new Date(data.lastUpdated).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="rounded-[10px] bg-base border border-white/[0.06] p-4 text-center">
            <div className="text-2xl font-bold text-white mb-1">{data.resourceAllocation.activeAgents}/{data.resourceAllocation.totalAgents}</div>
            <div className="text-xs text-[#94A3B8]">Agents Active</div>
          </div>
          <div className="rounded-[10px] bg-base border border-white/[0.06] p-4 text-center">
            <div className="text-2xl font-bold text-green-400 mb-1">SAR 0</div>
            <div className="text-xs text-[#94A3B8]">Monthly Burn</div>
          </div>
          <div className="rounded-[10px] bg-base border border-white/[0.06] p-4 text-center">
            <div className="text-2xl font-bold text-white mb-1">{data.criticalBlockers.length}</div>
            <div className="text-xs text-[#94A3B8]">Critical Blockers</div>
          </div>
          <div className="rounded-[10px] bg-base border border-white/[0.06] p-4 text-center">
            <div className="text-2xl font-bold text-white mb-1">{data.nextMilestones.length}</div>
            <div className="text-xs text-[#94A3B8]">Upcoming Milestones</div>
          </div>
        </div>
      </div>

      {/* Critical Blockers & Next Milestones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Critical Blockers */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">🚨 Critical Blockers</h3>
            <span className="text-xs px-2 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
              {data.criticalBlockers.filter(b => b.priority === 'P0').length} P0
            </span>
          </div>
          
          <div className="space-y-3">
            {(data.criticalBlockers || []).map((blocker) => (
              <div 
                key={blocker.id} 
                className="rounded-[10px] bg-base border border-white/[0.04] p-4 hover:border-white/[0.08] transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-bold text-white">{blocker.title}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.04] text-[#94A3B8]">
                        {blocker.project}
                      </span>
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: `${getPriorityColor(blocker.priority)}20`,
                          color: getPriorityColor(blocker.priority),
                          border: `1px solid ${getPriorityColor(blocker.priority)}40`
                        }}
                      >
                        {blocker.priority}
                      </span>
                    </div>
                  </div>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: `${getStatusColor(blocker.status)}20`,
                      color: getStatusColor(blocker.status),
                      border: `1px solid ${getStatusColor(blocker.status)}40`
                    }}
                  >
                    {blocker.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-[#94A3B8]">Impact: {blocker.impact}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#64748B]">Assigned to: {blocker.assignedTo}</span>
                    {blocker.assignedTo === 'Z' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        Requires CEO action
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next Milestones */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">🎯 Next Milestones</h3>
          
          <div className="space-y-3">
            {(data.nextMilestones || []).map((milestone) => (
              <div 
                key={milestone.id} 
                className="rounded-[10px] bg-base border border-white/[0.04] p-4 hover:border-white/[0.08] transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-bold text-white">{milestone.milestone}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.04] text-[#94A3B8]">
                        {milestone.project}
                      </span>
                      <span className="text-xs text-[#94A3B8]">Due: {milestone.dueDate}</span>
                    </div>
                  </div>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: `${getConfidenceColor(milestone.confidence)}20`,
                      color: getConfidenceColor(milestone.confidence),
                      border: `1px solid ${getConfidenceColor(milestone.confidence)}40`
                    }}
                  >
                    {milestone.confidence} confidence
                  </span>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-[#94A3B8]">Dependencies: {milestone.dependencies}</p>
                  {milestone.confidence === 'blocked' && (
                    <div className="flex items-center gap-1 text-xs text-red-400">
                      <span>⛔</span>
                      <span>Blocked - requires intervention</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Resource Allocation & Financial Snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Resource Allocation */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">🤖 Resource Allocation</h3>
          
          <div className="space-y-4">
            {Object.entries(((data.resourceAllocation || {}).byProject || {})).map(([project, allocation]) => (
              <div key={project} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{project}</span>
                    <span 
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: allocation.priority === 'highest' ? '#EF444420' : 
                                       allocation.priority === 'high' ? '#F59E0B20' : 
                                       allocation.priority === 'medium' ? '#3B82F620' : '#64748B20',
                        color: allocation.priority === 'highest' ? '#EF4444' : 
                               allocation.priority === 'high' ? '#F59E0B' : 
                               allocation.priority === 'medium' ? '#3B82F6' : '#64748B',
                        border: `1px solid ${allocation.priority === 'highest' ? '#EF444440' : 
                                 allocation.priority === 'high' ? '#F59E0B40' : 
                                 allocation.priority === 'medium' ? '#3B82F640' : '#64748B40'}`
                      }}
                    >
                      {allocation.priority} priority
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-white">{allocation.agents} agents</div>
                    <div className="text-xs text-[#64748B]">{formatNumber(allocation.tokens)} tokens</div>
                  </div>
                </div>
                <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full"
                    style={{
                      width: `${(allocation.tokens / Object.values(((data.resourceAllocation || {}).byProject || {})).reduce((sum, a) => sum + a.tokens, 0)) * 100}%`,
                      backgroundColor: project === 'Labor Portal' ? '#FF6B35' :
                                      project === 'Rafiq' ? '#00BFA5' :
                                      project === 'Muwafaq' ? '#4CAF50' :
                                      project === 'Sahha' ? '#448AFF' :
                                      project === 'Dashboard' ? '#8B5CF6' : '#64748B'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 pt-6 border-t border-white/[0.06]">
            <h4 className="text-md font-bold text-white mb-3">📊 Allocation Summary</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">
                  {data.resourceAllocation.activeAgents}
                </div>
                <div className="text-xs text-[#94A3B8]">Active Agents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-400 mb-1">
                  {formatNumber(Object.values(((data.resourceAllocation || {}).byProject || {})).reduce((sum, a) => sum + a.tokens, 0))}
                </div>
                <div className="text-xs text-[#94A3B8]">Monthly Tokens</div>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Snapshot */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">💰 Financial Snapshot</h3>
          
          {/* Zero Burn Highlight */}
          <div className="mb-6 p-4 rounded-[12px] bg-gradient-to-r from-[#22C55E]/20 to-[#86EFAC]/10 border border-[#22C55E]/30">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-white">SAR 0 Monthly Burn</div>
                <div className="text-sm text-[#94A3B8]">AI-only = Infinite Runway</div>
              </div>
              <div className="text-4xl">∞</div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#94A3B8]">Projected Revenue (15 customers)</span>
              <span className="text-lg font-bold text-green-400">
                SAR {formatNumber(data.financialSnapshot.projectedRevenue)}/month
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#94A3B8]">Break-even Customers</span>
              <span className="text-lg font-bold text-white">
                {data.financialSnapshot.breakEvenCustomers}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#94A3B8]">Current Paying Customers</span>
              <span className="text-lg font-bold text-white">
                {data.financialSnapshot.currentCustomers}
              </span>
            </div>
            
            <div className="border-t border-white/[0.06] pt-4">
              <h4 className="text-md font-bold text-white mb-3">📈 Progress to Break-even</h4>
              <div className="w-full h-3 bg-white/[0.04] rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-[#F59E0B] to-[#FCD34D]" 
                  style={{ 
                    width: `${(data.financialSnapshot.currentCustomers / data.financialSnapshot.breakEvenCustomers) * 100}%` 
                  }} 
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-[#64748B]">
                  {data.financialSnapshot.currentCustomers}/{data.financialSnapshot.breakEvenCustomers} customers
                </span>
                <span className="text-xs font-bold text-[#F59E0B]">
                  {Math.round((data.financialSnapshot.currentCustomers / data.financialSnapshot.breakEvenCustomers) * 100)}%
                </span>
              </div>
            </div>
            
            <div className="border-t border-white/[0.06] pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#94A3B8]">Next Funding Need</span>
                <span className="text-sm font-bold text-green-400">
                  {data.financialSnapshot.nextFundingNeed}
                </span>
              </div>
              <p className="text-xs text-[#64748B] mt-1">
                Zero burn rate eliminates need for external funding
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Strategic Priorities & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strategic Priorities */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">🎯 Strategic Priorities</h3>
          
          <div className="space-y-3">
            {(data.strategicPriorities || []).map((priority) => (
              <div 
                key={priority.id} 
                className="rounded-[10px] bg-base border border-white/[0.04] p-4 hover:border-white/[0.08] transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-bold text-white">{priority.priority}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-[#94A3B8]">Timeline: {priority.timeline}</span>
                      <span className="text-xs text-[#94A3B8]">Owner: {priority.owner}</span>
                    </div>
                  </div>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: `${getStatusColor(priority.status)}20`,
                      color: getStatusColor(priority.status),
                      border: `1px solid ${getStatusColor(priority.status)}40`
                    }}
                  >
                    {priority.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">⚡ Quick Actions</h3>
            <span className="text-xs px-2 py-1 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] border border-[#8B5CF6]/20">
              For {data.ceoName}
            </span>
          </div>
          
          <div className="space-y-3">
            {(data.quickActions || []).map((action) => (
              <div 
                key={action.id} 
                className="rounded-[10px] bg-base border border-white/[0.04] p-4 hover:border-white/[0.08] transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-sm font-bold text-white">{action.action}</h4>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: `${getUrgencyColor(action.urgency)}20`,
                      color: getUrgencyColor(action.urgency),
                      border: `1px solid ${getUrgencyColor(action.urgency)}40`
                    }}
                  >
                    {action.urgency}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#94A3B8]">Time required: {action.timeRequired}</span>
                  <button 
                    className="text-xs px-3 py-1 rounded-lg bg-[#8B5CF6] text-white hover:bg-[#7C3AED] transition-colors"
                    onClick={() => alert(`Action: ${action.action}`)}
                  >
                    Mark Complete
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 pt-6 border-t border-white/[0.06]">
            <p className="text-sm text-[#94A3B8]">
              <span className="font-bold text-white">Note:</span> All other operations are fully automated by AI agents.
              Your attention is only required for strategic decisions and unblocking critical path items.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}